import React from "react";
import { Chart } from "chart.js/auto";

const formatDateLabel = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString + "T00:00:00");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[date.getMonth()];
    const day = date.getDate();
    return `${month} ${day}`;
};

const queueDurationChartRef = React.createRef();
const playersQueuedPerDayChartRef = React.createRef();
const queueOutcomeDistributionChartRef = React.createRef();
const queueEfficiencyPerDayChartRef = React.createRef();
const matchmakingActivityChartRef = React.createRef();
const matchstatusDistributionChartRef = React.createRef();
const signupActivityChartRef = React.createRef();
const matchmakingCapacityChartRef = React.createRef();

export const monitoringCharts = [
    {
        id: "queue-duration",
        label: "Queue Duration",
        type: "line",
        ref: queueDurationChartRef,
        configFactory: (data, isDark = false) => ({
            type: "line",
            data: {
                labels: data.labels,
                datasets: [
                    {
                        data: data.avgDurations,
                        label: "AVG Duration",
                        borderColor: "#2563eb",
                        backgroundColor: "rgba(77, 77, 128, 0.1)",
                        borderWidth: 2,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: "#2563eb",
                        pointBorderColor: isDark ? "#111827" : "#ffffff",
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                    },
                    {
                        data: data.medianDurations,
                        label: "Median Duration",
                        borderColor: "#10b981",
                        backgroundColor: "rgba(77, 77, 128, 0.1)",
                        borderWidth: 2,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: "#10b981",
                        pointBorderColor: isDark ? "#111827" : "#ffffff",
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom", 
                        labels: {
                            usePointStyle: true, 
                            boxWidth: 8,
                            boxHeight: 8,
                            padding: 15,
                            color: isDark ? "#f9fafb" : "#000",
                        },
                    },
                    tooltip: {
                        enabled: true,
                        callbacks: {
                            label: (context) => {
                                const label = context.dataset.label || "";
                                const value = context.parsed.y || 0;
                                return `${label}: ${value}s`;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: { color: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", drawBorder: false },
                        ticks: { color: isDark ? "#9ca3af" : "#666", maxTicksLimit: 8 },
                    },
                    y: {
                        beginAtZero: true,
                        grid: { color: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", drawBorder: false },
                        ticks: { 
                            color: isDark ? "#9ca3af" : "#666", 
                            stepSize: 5,
                            callback: function(value) {
                                return value + 's';
                            },
                        },
                    },
                },
                interaction: { intersect: false, mode: "index" },
            },
        }),
        getData: (props) => {
            const queueData = props.queueData || [];
            return {
                labels: queueData.map(item => formatDateLabel(item.date)),
                avgDurations: queueData.map(item => item.avgDuration),
                medianDurations: queueData.map(item => item.medianDuration)
            };
        },
    },
    {
        id: "players-queued-per-day",
        label: "Players Queued Per Day",
        type: "bar",
        ref: playersQueuedPerDayChartRef,
        configFactory: (data, isDark = false) => ({
            type: "bar",
            data: {
                labels: data.labels,
                datasets: [
                    {
                        label: "Players Queued",
                        data: data.values,
                        backgroundColor: "#2563eb",
                        borderColor: "#1e40af",
                        borderWidth: 0,
                        borderRadius: {
                            topLeft: 5,
                            topRight: 5,
                            bottomLeft: 0,
                            bottomRight: 0,
                        },
                        borderSkipped: false,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: true,
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        padding: 10,
                        titleFont: {
                            size: 13,
                            weight: "bold",
                        },
                        bodyFont: {
                            size: 12,
                        },
                        callbacks: {
                            label: (context) => {
                                const value = context.parsed.y || 0;
                                return `Players: ${value}`;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: {
                            display: false,
                            drawBorder: false,
                        },
                        ticks: {
                            color: isDark ? "#f9fafb" : "#111",
                            font: {
                                size: 12,
                                weight: "500",
                            },
                        },
                    },
                    y: {
                        beginAtZero: true,
                        grid: {
                            color: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
                            drawBorder: false,
                        },
                        ticks: {
                            color: isDark ? "#9ca3af" : "#666",
                            font: {
                                size: 11,
                            },
                            precision: 0,
                        },
                    },
                },
                animation: {
                    duration: 800,
                    easing: "easeOutQuart",
                },
            },
        }),
        getData: (props) => {
            const data = props.playersQueuedPerDayData || [];
            return {
                labels: data.map(item => formatDateLabel(item.date)),
                values: data.map(item => item.playersQueued || 0)
            };
        },
    },
    {
        id: "queue-outcome-distribution",
        label: "Queue Outcome Distribution",
        type: "bar",
        ref: queueOutcomeDistributionChartRef,
        configFactory: (data, isDark = false) => ({
            type: "bar",
            data: {
                labels: data.labels,
                datasets: [
                    {
                        label: "Players Queued",
                        data: data.playersQueued,
                        backgroundColor: "#2563eb",
                        borderColor: isDark ? "#111827" : "#ffffff",
                        borderWidth: 0,
                        borderRadius: {
                            topLeft: 5,
                            topRight: 5,
                            bottomLeft: 0,
                            bottomRight: 0,
                        },
                        borderSkipped: false,
                    },
                    {
                        label: "Players Matched",
                        data: data.playersMatched,
                        backgroundColor: "#10b981",
                        borderColor: isDark ? "#111827" : "#ffffff",
                        borderWidth: 0,
                        borderRadius: {
                            topLeft: 5,
                            topRight: 5,
                            bottomLeft: 0,
                            bottomRight: 0,
                        },
                        borderSkipped: false,
                    },
                    {
                        label: "Players Left",
                        data: data.playersAbandonedQueue,
                        backgroundColor: "#e11d48",
                        borderColor: isDark ? "#111827" : "#ffffff",
                        borderWidth: 0,
                        borderRadius: {
                            topLeft: 5,
                            topRight: 5,
                            bottomLeft: 0,
                            bottomRight: 0,
                        },
                        borderSkipped: false,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: {
                            usePointStyle: true,
                            boxWidth: 8,
                            boxHeight: 8,
                            padding: 15,
                            color: isDark ? "#f9fafb" : "#000",
                        },
                    },
                    tooltip: {
                        enabled: true,
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        padding: 10,
                        titleFont: {
                            size: 13,
                            weight: "bold",
                        },
                        bodyFont: {
                            size: 12,
                        },
                        callbacks: {
                            label: (context) => {
                                const label = context.dataset.label || "";
                                const value = context.parsed.y || 0;
                                return `${label}: ${value}`;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: {
                            display: false,
                            drawBorder: false,
                        },
                        ticks: {
                            color: isDark ? "#f9fafb" : "#111",
                            font: {
                                size: 12,
                                weight: "500",
                            },
                        },
                    },
                    y: {
                        beginAtZero: true,
                        grid: {
                            color: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
                            drawBorder: false,
                        },
                        ticks: {
                            color: isDark ? "#9ca3af" : "#666",
                            font: {
                                size: 11,
                            },
                            precision: 0,
                        },
                    },
                },
                animation: {
                    duration: 800,
                    easing: "easeOutQuart",
                },
            },
        }),
        getData: (props) => {
            const data = props.queueOutcomeDistributionData || [];
            return {
                labels: data.map(item => formatDateLabel(item.date)),
                playersQueued: data.map(item => item.playersQueued || 0),
                playersMatched: data.map(item => item.playersMatched || 0),
                playersAbandonedQueue: data.map(item => item.playersAbandonedQueue || 0)
            };
        },
    },
    {
        id: "queue-efficiency-per-day",
        label: "Queue Efficiency Per Day",
        type: "bar",
        ref: queueEfficiencyPerDayChartRef,
        configFactory: (data, isDark = false) => ({
            type: "bar",
            data: {
                labels: data.labels,
                datasets: [
                    {
                        label: "Queue Efficiency",
                        data: data.values,
                        backgroundColor: "#2563eb",
                        borderColor: "#1e40af",
                        borderWidth: 0,
                        borderRadius: {
                            topLeft: 5,
                            topRight: 5,
                            bottomLeft: 0,
                            bottomRight: 0,
                        },
                        borderSkipped: false,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: true,
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        padding: 10,
                        titleFont: {
                            size: 13,
                            weight: "bold",
                        },
                        bodyFont: {
                            size: 12,
                        },
                        callbacks: {
                            label: (context) => {
                                const value = context.parsed.y || 0;
                                return `Efficiency: ${value}%`;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: {
                            display: false,
                            drawBorder: false,
                        },
                        ticks: {
                            color: isDark ? "#f9fafb" : "#111",
                            font: {
                                size: 12,
                                weight: "500",
                            },
                        },
                    },
                    y: {
                        beginAtZero: true,
                        max: 100,
                        grid: {
                            color: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
                            drawBorder: false,
                        },
                        ticks: {
                            color: isDark ? "#9ca3af" : "#666",
                            font: {
                                size: 11,
                            },
                            precision: 0,
                            callback: function(value) {
                                return value + '%';
                            },
                        },
                    },
                },
                animation: {
                    duration: 800,
                    easing: "easeOutQuart",
                },
            },
        }),
        getData: (props) => {
            const data = props.queueEfficiencyPerDayData || [];
            return {
                labels: data.map(item => formatDateLabel(item.date)),
                values: data.map(item => item.queueEfficiency || 0)
            };
        },
    },
    {
        id: "matchmaking-activity",
        label: "Matchmaking Activity Over Time",
        type: "line",
        ref: matchmakingActivityChartRef,
        configFactory: (data, isDark = false) => ({
            type: "line",
            data: {
                labels: data.labels,
                datasets: [
                    {
                        data: data.matchesCreated,
                        label: "Matches Created",
                        borderColor: "#2563eb",
                        backgroundColor: "rgba(77, 77, 128, 0.1)",
                        borderWidth: 2,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: "#2563eb",
                        pointBorderColor: isDark ? "#111827" : "#ffffff",
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                    },
                    {
                        data: data.matchesCancelled,
                        label: "Matches Cancelled",
                        borderColor: "#ef4444",
                        backgroundColor: "rgba(77, 77, 128, 0.1)",
                        borderWidth: 2,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: "#ef4444",
                        pointBorderColor: isDark ? "#111827" : "#ffffff",
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom", 
                        labels: {
                            usePointStyle: true, 
                            boxWidth: 8,
                            boxHeight: 8,
                            padding: 15,
                            color: isDark ? "#f9fafb" : "#000",
                        },
                    },
                },
                scales: {
                    x: {
                        grid: { color: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", drawBorder: false },
                        ticks: { color: isDark ? "#9ca3af" : "#666", maxTicksLimit: 8 },
                    },
                    y: {
                        beginAtZero: true,
                        grid: { color: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", drawBorder: false },
                        ticks: { color: isDark ? "#9ca3af" : "#666", stepSize: 5 },
                    },
                },
                interaction: { intersect: false, mode: "index" },
            },
        }),
        getData: (props) => props.matchesData,
    },
    {
        id: "matchstatus-distribution",
        label: "Matchduration Distribution",
        type: "bar",
        ref: matchstatusDistributionChartRef,
        configFactory: (data, isDark = false) => ({
            type: "bar",
            data: {
                labels: data.labels,
                datasets: [
                    {
                        label: "Matches",
                        data: data.values,
                        backgroundColor: "#2563eb",
                        borderColor: "#1e40af",
                        borderWidth: 0,
                        borderRadius: {
                            topLeft: 5,
                            topRight: 5,
                            bottomLeft: 0,
                            bottomRight: 0,
                        },
                        borderSkipped: false,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: true,
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        padding: 10,
                        titleFont: {
                            size: 13,
                            weight: "bold",
                        },
                        bodyFont: {
                            size: 12,
                        },
                        callbacks: {
                            label: (context) => {
                                const value = context.parsed.y || 0;
                                return `Matches: ${value}`;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: {
                            display: false,
                            drawBorder: false,
                        },
                        ticks: {
                            color: isDark ? "#f9fafb" : "#111",
                            font: {
                                size: 12,
                                weight: "500",
                            },
                        },
                    },
                    y: {
                        beginAtZero: true,
                        grid: {
                            color: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)",
                            drawBorder: false,
                        },
                        ticks: {
                            color: isDark ? "#9ca3af" : "#666",
                            font: {
                                size: 11,
                            },
                            precision: 0,
                        },
                    },
                },
                animation: {
                    duration: 800,
                    easing: "easeOutQuart",
                },
            },
        }),
        getData: (props) => {
            const durationData = props.matchDurationData || {};
            const labels = ["0-10m", "10-20m", "20-30m", "30-40m", "40-50m", "50+m"];
            const values = [
                durationData["0-10"] || 0,
                durationData["10-20"] || 0,
                durationData["20-30"] || 0,
                durationData["30-40"] || 0,
                durationData["40-50"] || 0,
                durationData["50+"] || 0,
            ];
            return { labels, values };
        },
    },
    {
        id: "signup-activity",
        label: "Ouro Signup Activity",
        type: "line",
        ref: signupActivityChartRef,
        configFactory: (data, isDark = false) => ({
            type: "line",
            data: {
                labels: data.labels,
                datasets: [
                    {
                        data: data.data,
                        label: "Signups",
                        borderColor: "#2563eb",
                        backgroundColor: "rgba(77, 77, 128, 0.1)",
                        borderWidth: 2,
                        fill: true,
                        tension: 0.4,
                        pointBackgroundColor: "#2563eb",
                        pointBorderColor: isDark ? "#111827" : "#ffffff",
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                },
                scales: {
                    x: {
                        grid: { color: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", drawBorder: false },
                        ticks: { color: isDark ? "#9ca3af" : "#666", maxTicksLimit: 8 },
                    },
                    y: {
                        beginAtZero: true,
                        grid: { color: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", drawBorder: false },
                        ticks: { color: isDark ? "#9ca3af" : "#666", stepSize: 5 },
                    },
                },
                interaction: { intersect: false, mode: "index" },
            },
        }),
        
        getData: (props) => props.newPlayersData,
    },
    {
        id: "matchmaking-capacity",
        label: "Matchmaking Capacity Pressure",
        type: "line",
        ref: matchmakingCapacityChartRef,
        configFactory: (data, isDark = false) => ({
            type: "line",
            data: {
                labels: data.labels,
                datasets: [
                    {
                        data: data.blockedGroups,
                        label: "Blocked Groups",
                        borderColor: "#e11d48",
                        backgroundColor: "rgba(225, 29, 72, 0.08)",
                        borderWidth: 2,
                        fill: true,
                        tension: 0.35,
                        pointBackgroundColor: "#e11d48",
                        pointBorderColor: isDark ? "#111827" : "#ffffff",
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                    },
                    {
                        data: data.heldGroupsMaterialized,
                        label: "Held Groups Materialized",
                        borderColor: "#10b981",
                        backgroundColor: "rgba(16, 185, 129, 0.08)",
                        borderWidth: 2,
                        fill: true,
                        tension: 0.35,
                        pointBackgroundColor: "#10b981",
                        pointBorderColor: isDark ? "#111827" : "#ffffff",
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: {
                            usePointStyle: true,
                            boxWidth: 8,
                            boxHeight: 8,
                            padding: 15,
                            color: isDark ? "#f9fafb" : "#000",
                        },
                    },
                },
                scales: {
                    x: {
                        grid: { color: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", drawBorder: false },
                        ticks: { color: isDark ? "#9ca3af" : "#666", maxTicksLimit: 8 },
                    },
                    y: {
                        beginAtZero: true,
                        grid: { color: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", drawBorder: false },
                        ticks: { color: isDark ? "#9ca3af" : "#666", precision: 0 },
                    },
                },
                interaction: { intersect: false, mode: "index" },
            },
        }),
        getData: (props) => {
            const data = props.matchmakingCapacityTrendData || [];
            return {
                labels: data.map((item) => formatDateLabel(item.date)),
                blockedGroups: data.map((item) => item.blockedGroups || 0),
                heldGroupsMaterialized: data.map((item) => item.heldGroupsMaterialized || 0),
            };
        },
    },
];
